import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  MODOS_ATUALIZACAO_CUSTO,
  novoParametroFormSchema,
  type ModoAtualizacaoCusto,
  type NovoParametroDto,
  type NovoParametroFormEntrada,
  type ParametroEmpresaResponse,
} from '@triade/contracts';
import { usePode } from '@/api/me';
import { useNovoParametro, useParametros } from '@/api/parametros-empresa';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { ExibicaoEmpresa, ReformaFormacaoEmpresa } from './exibicao-empresa';
import { AcoesCopilotoEmpresa } from './acoes-copiloto-empresa';
import { AtividadesEmpresa } from './atividades-empresa';
import { formatarData, formatarFracaoPercentual, formatarTaxa, fracaoParaPercentualTexto } from '@/lib/format';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

export const ROTULO_MODO: Record<ModoAtualizacaoCusto, string> = {
  AUTOMATICO: 'Automático',
  COM_APROVACAO: 'Com aprovação',
};

export const ROTULO_POLITICA: Record<'NEGOCIADO' | 'TABELA', string> = {
  TABELA: 'Tabela',
  NEGOCIADO: 'Negociado',
};

type Form = UseFormReturn<NovoParametroFormEntrada, unknown, NovoParametroDto>;

export function hojeLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Dia seguinte a uma data ISO (so calendario). */
export function diaSeguinte(iso: string): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}

/** Vigencia que vale em `hoje`: comecou e ainda nao fechou. A lista vem da mais nova para a mais antiga. */
export function vigenteEm(linhas: readonly ParametroEmpresaResponse[], hoje: string): ParametroEmpresaResponse | undefined {
  return linhas.find((l) => l.validFrom <= hoje && (l.validTo === null || l.validTo > hoje));
}

/**
 * Valores iniciais do formulario "nova vigencia": a vigente em percentual (texto). A data sugerida e hoje; se ja existe
 * vigencia que comeca hoje (ou depois), o dia seguinte ao inicio da mais nova (a API nao aceita data igual nem anterior).
 */
export function valoresIniciais(linhas: readonly ParametroEmpresaResponse[], hoje: string = hojeLocal()): NovoParametroFormEntrada {
  const base = vigenteEm(linhas, hoje) ?? linhas[0];
  const maisNova = linhas[0];
  const data = maisNova && maisNova.validFrom >= hoje ? diaSeguinte(maisNova.validFrom) : hoje;
  return {
    validFrom: data,
    fixedExpenseRate: fracaoParaPercentualTexto(base?.fixedExpenseRate),
    financialCostRate: fracaoParaPercentualTexto(base?.financialCostRate),
    lucroDesejadoPadrao: fracaoParaPercentualTexto(base?.lucroDesejadoPadrao),
    lucroMinimoPadrao: fracaoParaPercentualTexto(base?.lucroMinimoPadrao),
    validadeDiasPadrao: String(base?.validadeDiasPadrao ?? 15),
    modoAtualizacaoCusto: base?.modoAtualizacaoCusto ?? 'COM_APROVACAO',
    // Sem vigencia anterior, a politica padrao e TABELA; com vigencia, a nova parte da que vale (so muda se o usuario trocar).
    politicaPreco: base?.politicaPreco ?? 'TABELA',
    // Padrao desmarcado (fixo absorvido); com vigencia, a nova parte da que vale.
    processoCustoVariavel: base?.processoCustoVariavel ?? false,
    // Padrao SIM (transicao 2027-2032); e as obrigacoes acessorias padrao SIM (QT1).
    cbsIbsNaBaseIcms: base?.cbsIbsNaBaseIcms ?? true,
    // QT18: padrao SIM (transicao 2027-2032), como o do ICMS.
    cbsIbsNaBaseIss: base?.cbsIbsNaBaseIss ?? true,
    cumpreObrigacoesAcessorias: base?.cumpreObrigacoesAcessorias ?? true,
    limiarAlertaLucro: base ? fracaoParaPercentualTexto(base.limiarAlertaLucro) : '2',
    encargosSociais: fracaoParaPercentualTexto(base?.encargosSociais),
    diasMes: base?.diasMes ?? '',
    horasDia: base?.horasDia ?? '',
    ociosidade: fracaoParaPercentualTexto(base?.ociosidade),
    motivo: '',
  };
}

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

function Campo({ form, nome, rotulo, ajuda, desabilitado, inputMode = 'decimal', type }: {
  form: Form; nome: Exclude<keyof NovoParametroFormEntrada, 'modoAtualizacaoCusto'>; rotulo: string; ajuda?: string; desabilitado: boolean; inputMode?: 'decimal' | 'numeric' | 'text'; type?: string;
}) {
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={`par-${nome}`} className={ROTULO}>
        {rotulo}
      </Label>
      <Input
        id={`par-${nome}`}
        type={type}
        inputMode={inputMode}
        disabled={desabilitado}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `par-${nome}-erro` : undefined}
        {...form.register(nome)}
      />
      <MensagemCampo id={`par-${nome}-erro`} mensagem={erro} />
      {ajuda && <p className="mt-1 text-caption text-ink-muted">{ajuda}</p>}
    </div>
  );
}

/** Aba "Parametros" da tela Empresa: vigencia atual, formulario de nova vigencia (append-only) e historico. */
export function ParametrosEmpresa({ editavel }: { editavel: boolean }) {
  const lista = useParametros({ limit: 200 });
  if (lista.isError) return <ErroFormacao erro={lista.error} onTentar={() => void lista.refetch()} />;
  if (!lista.data) return <Vazio papel="status">Carregando parâmetros da empresa…</Vazio>;
  return <Editor linhas={lista.data} editavel={editavel} />;
}

function Editor({ linhas, editavel }: { linhas: ParametroEmpresaResponse[]; editavel: boolean }) {
  const hoje = hojeLocal();
  const base = useMemo(() => valoresIniciais(linhas, hoje), [linhas, hoje]);
  const vigente = vigenteEm(linhas, hoje);
  const form = useForm<NovoParametroFormEntrada, unknown, NovoParametroDto>({ resolver: zodResolver(novoParametroFormSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields } = form.formState;
  const novo = useNovoParametro();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const bloqueio = useBlockerDirty(isDirty);
  const { pode } = usePode();

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const gravar = async (valores: NovoParametroDto) => {
    setErro(null);
    try {
      const gravada = await novo.mutateAsync(valores);
      // O formulario passa a refletir o gravado (a nova vigencia vira a base; a proxima data sugerida avanca).
      const proximas = [gravada, ...linhas].sort((a, b) => b.validFrom.localeCompare(a.validFrom));
      form.reset(valoresIniciais(proximas, hoje));
      setSalvoEm(new Date());
    } catch (e) {
      setErro(mensagemErro(e));
    }
  };
  const gravarAgora = () => void form.handleSubmit(gravar, () => setErro('Corrija os campos destacados'))();
  const desabilitado = !editavel;
  const alterados = Object.keys(dirtyFields).filter((c) => c !== 'validFrom').length;
  const erroModo = form.formState.errors.modoAtualizacaoCusto?.message;

  return (
    <div className="flex flex-col gap-5">
      <AtividadesEmpresa editavel={editavel} />
      <ExibicaoEmpresa editavel={editavel} />
      <ReformaFormacaoEmpresa editavel={editavel} />
      <AcoesCopilotoEmpresa editavel={pode('parametros.editar')} />
      <section aria-label="Vigência atual" className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-title font-semibold">Vigência atual</h2>
        {vigente ? (
          <>
            <p className="mt-1 text-small text-ink-secondary" data-testid="vigente-desde">
              Vale desde {formatarData(vigente.validFrom)}
              {vigente.criadoPor ? `, registrada por ${vigente.criadoPor}` : ''}.
            </p>
            <dl className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-6 gap-y-3 text-small">
              <Item rotulo="Despesa fixa" valor={formatarTaxa(vigente.fixedExpenseRate)} />
              <Item rotulo="Taxa financeira a.m." valor={formatarTaxa(vigente.financialCostRate)} />
              <Item rotulo="Lucro desejado padrão" valor={vigente.lucroDesejadoPadrao ? formatarTaxa(vigente.lucroDesejadoPadrao) : 'não definido'} />
              <Item rotulo="Lucro mínimo padrão" valor={vigente.lucroMinimoPadrao ? formatarTaxa(vigente.lucroMinimoPadrao) : 'não definido'} />
              <Item rotulo="Validade da proposta" valor={`${vigente.validadeDiasPadrao} dias`} />
              <Item rotulo="Atualização de custo" valor={ROTULO_MODO[vigente.modoAtualizacaoCusto]} />
              <Item rotulo="Política de preço" valor={ROTULO_POLITICA[vigente.politicaPreco]} />
              <Item rotulo="Processo é custo variável" valor={vigente.processoCustoVariavel ? 'Sim' : 'Não'} />
              <Item rotulo="CBS/IBS na base do ICMS" valor={vigente.cbsIbsNaBaseIcms ? 'Sim' : 'Não'} />
              <Item rotulo="CBS/IBS na base do ISS" valor={vigente.cbsIbsNaBaseIss ? 'Sim' : 'Não'} />
              <Item rotulo="Cumpre obrigações acessórias" valor={vigente.cumpreObrigacoesAcessorias ? 'Sim' : 'Não'} />
              <Item rotulo="Limiar de alerta de lucro" valor={`${fracaoParaPercentualTexto(vigente.limiarAlertaLucro)} p.p.`} />
            </dl>
          </>
        ) : (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            {linhas.length === 0 ? 'Nenhuma vigência cadastrada. Registre a primeira abaixo.' : 'Não há vigência para hoje. A próxima começa em ' + formatarData(linhas[0]?.validFrom) + '.'}
          </p>
        )}
        <p className="mt-3 text-caption text-ink-muted">
          O lucro desejado e o mínimo padrão valem para as tabelas de preço que não definem o próprio valor (a tela mostra a origem: “da tabela” ou “padrão da empresa”).
        </p>
      </section>

      {!editavel && (
        <p role="note" data-testid="aviso-parametros-somente-leitura" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Seu papel só consulta os parâmetros: financeiro e administradores registram novas vigências.
        </p>
      )}

      <form onSubmit={(e) => { e.preventDefault(); gravarAgora(); }} noValidate aria-label="Nova vigência" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
        <div>
          <h2 className="text-title font-semibold">Nova vigência</h2>
          <p className="mt-1 text-small text-ink-secondary">
            Cada alteração vira uma nova linha: nada é editado nem retroativo. O que já foi calculado continua como estava; a data de início não pode ser anterior a hoje.
          </p>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
          <Campo form={form} nome="validFrom" rotulo="Vale a partir de" desabilitado={desabilitado} type="date" inputMode="text" />
          <Campo form={form} nome="fixedExpenseRate" rotulo="Despesa fixa (%)" desabilitado={desabilitado} />
          <Campo form={form} nome="financialCostRate" rotulo="Taxa financeira (% a.m.)" desabilitado={desabilitado} />
          <Campo form={form} nome="encargosSociais" rotulo="Encargos sociais (%)" desabilitado={desabilitado} ajuda="Vazio = 0. Não se aplica a terceirizado." />
          <Campo form={form} nome="lucroDesejadoPadrao" rotulo="Lucro desejado padrão (%)" desabilitado={desabilitado} ajuda="Vazio = a empresa não define; a tabela precisa ter o próprio." />
          <Campo form={form} nome="lucroMinimoPadrao" rotulo="Lucro mínimo padrão (%)" desabilitado={desabilitado} ajuda="Vazio = mínimo 0 nas tabelas que não definem." />
          <Campo form={form} nome="validadeDiasPadrao" rotulo="Validade padrão da proposta (dias)" desabilitado={desabilitado} inputMode="numeric" />
          <Campo form={form} nome="diasMes" rotulo="Dias por mês" desabilitado={desabilitado} ajuda="Vazio = 22." />
          <Campo form={form} nome="horasDia" rotulo="Horas por dia" desabilitado={desabilitado} ajuda="Vazio = 8." />
          <Campo form={form} nome="ociosidade" rotulo="Ociosidade dos centros (%)" desabilitado={desabilitado} ajuda="Reduz os minutos disponíveis no custo por minuto; 0% = sem ociosidade" />
          <div>
            <Label htmlFor="par-modoAtualizacaoCusto" className={ROTULO}>
              Atualização de custo
            </Label>
            <select
              id="par-modoAtualizacaoCusto"
              disabled={desabilitado}
              aria-invalid={erroModo ? true : undefined}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
              {...form.register('modoAtualizacaoCusto')}
            >
              {MODOS_ATUALIZACAO_CUSTO.map((m) => (
                <option key={m} value={m}>
                  {ROTULO_MODO[m]}
                </option>
              ))}
            </select>
            <MensagemCampo id="par-modoAtualizacaoCusto-erro" mensagem={erroModo} />
            <p className="mt-1 text-caption text-ink-muted">Com aprovação, mudança de custo espera o financeiro aplicar (próxima etapa).</p>
          </div>
          <Campo form={form} nome="limiarAlertaLucro" rotulo="Limiar de alerta de lucro (p.p.)" desabilitado={desabilitado} ajuda="Queda de lucro, em pontos percentuais, que dispara o alerta." />
          <div>
            <Label htmlFor="par-politicaPreco" className={ROTULO}>
              Política de preço
            </Label>
            <select
              id="par-politicaPreco"
              disabled={desabilitado}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
              {...form.register('politicaPreco')}
            >
              <option value="TABELA">Tabela — praticado da lista publicada</option>
              <option value="NEGOCIADO">Negociado — o orçamento parte do sugerido</option>
            </select>
            <p className="mt-1 text-caption text-ink-muted">Negociado esconde o praticado na Formação e na Lista.</p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="par-processoCustoVariavel" className="flex items-center gap-2 text-small text-ink-body">
              <input
                id="par-processoCustoVariavel"
                type="checkbox"
                disabled={desabilitado}
                className="size-4 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('processoCustoVariavel')}
              />
              Processo é custo variável
            </label>
            <p className="text-caption text-ink-muted">Marque para tratar o custo de processo — minutos × CPM — como variável na margem de contribuição; padrão: fixo absorvido.</p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="par-cbsIbsNaBaseIcms" className="flex items-center gap-2 text-small text-ink-body">
              <input
                id="par-cbsIbsNaBaseIcms"
                type="checkbox"
                disabled={desabilitado}
                aria-describedby="par-cbsIbsNaBaseIcms-ajuda"
                className="size-4 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('cbsIbsNaBaseIcms')}
              />
              CBS/IBS na base do ICMS
            </label>
            <p id="par-cbsIbsNaBaseIcms-ajuda" className="text-caption text-ink-muted">
              Padrão SIM, a partir de 2027 (transição). A lei é silente; a SEFAZ-SP (RC 33.083/2026) entende que integram quando exigíveis; decisões judiciais divergem em 2026 e o PLP 16/2025 está pendente. Sem liminar, o preço segue o fisco.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="par-cbsIbsNaBaseIss" className="flex items-center gap-2 text-small text-ink-body">
              <input
                id="par-cbsIbsNaBaseIss"
                type="checkbox"
                disabled={desabilitado}
                aria-describedby="par-cbsIbsNaBaseIss-ajuda"
                className="size-4 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('cbsIbsNaBaseIss')}
              />
              CBS/IBS na base do ISS
            </label>
            <p id="par-cbsIbsNaBaseIss-ajuda" className="text-caption text-ink-muted">
              Vale só na transição (2027–2032); em 2026 e a partir de 2033 não se aplica.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="par-cumpreObrigacoesAcessorias" className="flex items-center gap-2 text-small text-ink-body">
              <input
                id="par-cumpreObrigacoesAcessorias"
                type="checkbox"
                disabled={desabilitado}
                className="size-4 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('cumpreObrigacoesAcessorias')}
              />
              Cumpre as obrigações acessórias (CBS/IBS de 2026 apenas informativos)
            </label>
          </div>
        </div>
        <Campo form={form} nome="motivo" rotulo="Motivo (opcional)" desabilitado={desabilitado} inputMode="text" />
      </form>

      <section aria-label="Histórico de vigências" className="overflow-x-auto rounded-2xl border border-border bg-card p-5">
        <h2 className="text-title font-semibold">Histórico</h2>
        {linhas.length === 0 ? (
          <p className="mt-2 text-small text-ink-muted">Ainda não há vigências.</p>
        ) : (
          <table className="mt-3 w-full min-w-[1280px] border-collapse text-small">
            <thead>
              <tr className="border-b border-border text-left text-caption text-ink-muted">
                {['Início', 'Fim', 'Desp. fixa', 'Taxa fin.', 'Encargos', 'Lucro desej.', 'Lucro mín.', 'Validade', 'Dias/mês', 'Horas/dia', 'Ociosidade', 'Custo', 'Política', 'Processo var.', 'CBS/IBS na base ICMS', 'CBS/IBS na base ISS', 'Obrig. acessórias', 'Alerta', 'Registrada por', 'Motivo'].map((c) => (
                  <th key={c} scope="col" className="px-2 py-2 font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.id} data-vigente={l.id === vigente?.id ? 'true' : undefined} className={l.id === vigente?.id ? 'border-b border-border bg-line-soft' : 'border-b border-border'}>
                  <td className="px-2 py-2 tabular">{formatarData(l.validFrom)}</td>
                  <td className="px-2 py-2 tabular">{l.validTo ? formatarData(l.validTo) : l.id === vigente?.id ? 'vigente' : 'em aberto'}</td>
                  <td className="px-2 py-2 tabular">{formatarTaxa(l.fixedExpenseRate)}</td>
                  <td className="px-2 py-2 tabular">{formatarTaxa(l.financialCostRate)}</td>
                  <td className="px-2 py-2 tabular">{formatarTaxa(l.encargosSociais)}</td>
                  <td className="px-2 py-2 tabular">{l.lucroDesejadoPadrao ? formatarTaxa(l.lucroDesejadoPadrao) : '—'}</td>
                  <td className="px-2 py-2 tabular">{l.lucroMinimoPadrao ? formatarTaxa(l.lucroMinimoPadrao) : '—'}</td>
                  <td className="px-2 py-2 tabular">{l.validadeDiasPadrao} dias</td>
                  <td className="px-2 py-2 tabular" data-testid="historico-dias-mes">{l.diasMes}</td>
                  <td className="px-2 py-2 tabular" data-testid="historico-horas-dia">{l.horasDia}</td>
                  <td className="px-2 py-2 tabular" data-testid="historico-ociosidade">{formatarFracaoPercentual(l.ociosidade)}</td>
                  <td className="px-2 py-2">{ROTULO_MODO[l.modoAtualizacaoCusto]}</td>
                  <td className="px-2 py-2" data-testid="historico-politica">{ROTULO_POLITICA[l.politicaPreco]}</td>
                  <td className="px-2 py-2" data-testid="historico-processo-variavel">{l.processoCustoVariavel ? 'Sim' : 'Não'}</td>
                  <td className="px-2 py-2" data-testid="historico-cbs-ibs-base">{l.cbsIbsNaBaseIcms ? 'Sim' : 'Não'}</td>
                  <td className="px-2 py-2" data-testid="historico-cbs-ibs-base-iss">{l.cbsIbsNaBaseIss ? 'Sim' : 'Não'}</td>
                  <td className="px-2 py-2" data-testid="historico-obrigacoes">{l.cumpreObrigacoesAcessorias ? 'Sim' : 'Não'}</td>
                  <td className="px-2 py-2 tabular">{fracaoParaPercentualTexto(l.limiarAlertaLucro)} p.p.</td>
                  <td className="px-2 py-2">{l.criadoPor ?? '—'}</td>
                  <td className="px-2 py-2">{l.motivo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editavel && (
        <RecordToolbar
          dirty={isDirty}
          saving={novo.isPending}
          savedAt={salvoEm}
          erro={erro}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? Math.max(alterados, 1) : undefined}
          onSave={gravarAgora}
          onUndo={() => {
            form.reset(base);
            setErro(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </div>
  );
}

function Item({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-caption text-ink-muted">{rotulo}</dt>
      <dd className="tabular font-medium">{valor}</dd>
    </div>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
